import { DIMENSION_TITLES, MEASURE_TITLES } from '../analytics'
import { MEASURE_DEFINITION_BY_TITLE } from '../analyticsDefinitions'
import { EXPLORER_TYPE } from '../renderTypes'
import { useCustomizationStore } from '~/stores/customization'
import { getTableViewsFilters } from '~/services/explore'
import { OrderValue } from '~/types/cube'
import { SourceType } from '~/types/sources'
import { CustomizationField } from '~/types/customization'
import {
  DashboardModuleType,
  type DashboardConfig,
  type ModuleConfig
} from '~/types/dashboards'

export const createWorkforceDashboardConfig = (): DashboardConfig => {
  const customizationStore = useCustomizationStore()
  const employeeGroupDimension = customizationStore.getCustomizationByField(
    CustomizationField.EMPLOYEE_GROUP
  )

  const isManagerDataAvailable = customizationStore.isManagerDataAvailable

  return [
    {
      titleKey: 'dashboard.keyFigures',
      content: [
        {
          moduleType: DashboardModuleType.SINGLE,
          options: {
            config: {
              titleKey: 'dashboard.workforce.headcount.title',
              fillTrendMissingData: true,
              exploreLink: {
                measure: MEASURE_TITLES.EMPLOYEE_COUNT_END,
                dimension: employeeGroupDimension,
                type: EXPLORER_TYPE.DOUGHNUT
              },
              infoContent:
                MEASURE_DEFINITION_BY_TITLE[MEASURE_TITLES.EMPLOYEE_COUNT_END]
            },
            query: {
              measures: [MEASURE_TITLES.EMPLOYEE_COUNT_END]
            }
          }
        },
        {
          moduleType: DashboardModuleType.SINGLE,
          options: {
            config: {
              titleKey: 'dashboard.workforce.arrivals.title',
              exploreLink: {
                measure: MEASURE_TITLES.EMPLOYEE_COUNT_ARRIVAL,
                type: EXPLORER_TYPE.BAR
              },
              infoContent:
                MEASURE_DEFINITION_BY_TITLE[
                  MEASURE_TITLES.EMPLOYEE_COUNT_ARRIVAL
                ]
            },
            query: {
              measures: [MEASURE_TITLES.EMPLOYEE_COUNT_ARRIVAL]
            }
          }
        },
        {
          moduleType: DashboardModuleType.SINGLE,
          options: {
            config: {
              titleKey: 'dashboard.workforce.departures.title',
              exploreLink: {
                measure: MEASURE_TITLES.EMPLOYEE_COUNT_DEPARTURE_FINANCE,
                type: EXPLORER_TYPE.BAR
              },
              infoContent:
                MEASURE_DEFINITION_BY_TITLE[
                  MEASURE_TITLES.EMPLOYEE_COUNT_DEPARTURE_FINANCE
                ]
            },
            query: {
              measures: [MEASURE_TITLES.EMPLOYEE_COUNT_DEPARTURE_FINANCE]
            }
          }
        },
        {
          moduleType: DashboardModuleType.SINGLE,
          options: {
            config: {
              titleKey: 'dashboard.workforce.fteHR.title',
              fillTrendMissingData: true,
              exploreLink: {
                measure: MEASURE_TITLES.EMPLOYEE_COUNT_END_FTE_HR,
                type: EXPLORER_TYPE.KPI
              },
              infoContent:
                MEASURE_DEFINITION_BY_TITLE[
                  MEASURE_TITLES.EMPLOYEE_COUNT_END_FTE_HR
                ]
            },
            query: {
              measures: [MEASURE_TITLES.EMPLOYEE_COUNT_END_FTE_HR]
            }
          }
        },
        {
          moduleType: DashboardModuleType.TOP,
          options: {
            config: {
              titleKey: 'dashboard.workforce.lastNewEmployees.title',
              columnOrder: [
                DIMENSION_TITLES.EMPLOYEE_DISPLAY_NAME_WITH_ID,
                DIMENSION_TITLES.DATE_DAY
              ],
              columnsTranslationPrefix:
                'dashboard.workforce.lastNewEmployees.columns',
              exploreLink: {
                measure: MEASURE_TITLES.EMPLOYEE_COUNT_ARRIVAL,
                type: EXPLORER_TYPE.LIST
              }
            },
            query: {
              measures: [MEASURE_TITLES.EMPLOYEE_COUNT_ARRIVAL],
              dimensions: [
                DIMENSION_TITLES.EMPLOYEE_DISPLAY_NAME_WITH_ID,
                DIMENSION_TITLES.DATE_DAY
              ],
              filters: getTableViewsFilters(
                MEASURE_TITLES.EMPLOYEE_COUNT_ARRIVAL
              ),
              order: [[DIMENSION_TITLES.DATE_DAY, OrderValue.DESC]],
              limit: 5
            }
          }
        },
        {
          moduleType: DashboardModuleType.TOP,
          options: {
            config: {
              titleKey: 'dashboard.workforce.lastLeftEmployees.title',
              columnOrder: [
                DIMENSION_TITLES.EMPLOYEE_DISPLAY_NAME_WITH_ID,
                DIMENSION_TITLES.EMPLOYEE_DAY_AFTER_DEPARTURE_DATE
              ],
              columnsTranslationPrefix:
                'dashboard.workforce.lastLeftEmployees.columns',
              exploreLink: {
                measure: MEASURE_TITLES.EMPLOYEE_COUNT_DEPARTURE_FINANCE,
                type: EXPLORER_TYPE.LIST
              }
            },
            query: {
              measures: [MEASURE_TITLES.EMPLOYEE_COUNT_DEPARTURE_FINANCE],
              dimensions: [
                DIMENSION_TITLES.EMPLOYEE_DISPLAY_NAME_WITH_ID,
                DIMENSION_TITLES.EMPLOYEE_DAY_AFTER_DEPARTURE_DATE
              ],
              filters: getTableViewsFilters(
                MEASURE_TITLES.EMPLOYEE_COUNT_DEPARTURE_FINANCE
              ),
              order: [
                [
                  DIMENSION_TITLES.EMPLOYEE_DAY_AFTER_DEPARTURE_DATE,
                  OrderValue.DESC
                ]
              ],
              limit: 5
            }
          }
        },
        {
          moduleType: DashboardModuleType.SINGLE,
          options: {
            config: {
              withoutTrend: true,
              titleKey: 'dashboard.workforce.tenure.title',
              exploreLink: {
                measure: MEASURE_TITLES.EMPLOYEE_AVERAGE_TENURE,
                type: EXPLORER_TYPE.LIST
              },
              benchmarkLink: {
                measure: MEASURE_TITLES.EMPLOYEE_AVERAGE_TENURE
              },
              infoContent:
                MEASURE_DEFINITION_BY_TITLE[
                  MEASURE_TITLES.EMPLOYEE_AVERAGE_TENURE
                ]
            },
            query: {
              measures: [MEASURE_TITLES.EMPLOYEE_AVERAGE_TENURE]
            }
          }
        }
      ]
    },
    {
      titleKey: 'dashboard.moreData',
      content: [
        {
          moduleType: DashboardModuleType.SINGLE,
          options: {
            config: {
              titleKey: 'dashboard.workforce.employeeGrowthRate.title',
              exploreLink: {
                measure: MEASURE_TITLES.EMPLOYEE_GROWTH_RATE,
                type: EXPLORER_TYPE.BAR
              },
              infoContent:
                MEASURE_DEFINITION_BY_TITLE[MEASURE_TITLES.EMPLOYEE_GROWTH_RATE]
            },
            query: {
              measures: [MEASURE_TITLES.EMPLOYEE_GROWTH_RATE]
            }
          }
        },
        {
          moduleType: DashboardModuleType.SINGLE,
          options: {
            config: {
              titleKey: 'dashboard.workforce.countAverage.title',
              exploreLink: {
                measure: MEASURE_TITLES.EMPLOYEE_COUNT_AVERAGE,
                type: EXPLORER_TYPE.KPI
              },
              infoContent:
                MEASURE_DEFINITION_BY_TITLE[
                  MEASURE_TITLES.EMPLOYEE_COUNT_AVERAGE
                ],
              withoutTrend: true
            },
            query: {
              measures: [MEASURE_TITLES.EMPLOYEE_COUNT_AVERAGE]
            }
          }
        },
        ...(isManagerDataAvailable
          ? [
              {
                moduleType: DashboardModuleType.DOUBLE,
                options: {
                  config: {
                    titleKey: 'dashboard.workforce.managers.title',
                    firstSubtitleKey:
                      'dashboard.workforce.managers.total.title',
                    secondSubtitleKey:
                      'dashboard.workforce.managers.ratio.title',
                    exploreLink: {
                      measure: MEASURE_TITLES.EMPLOYEE_COUNT_END_MANAGER,
                      type: EXPLORER_TYPE.LIST
                    },
                    benchmarkLink: {
                      measure: MEASURE_TITLES.EMPLOYEE_RATIO_MANAGER
                    },
                    infoContent: {
                      definition:
                        'dashboard.workforce.managers.info.definition',
                      source: SourceType.HRIS,
                      insight: 'dashboard.workforce.managers.info.insight'
                    }
                  },
                  firstQuery: {
                    measures: [MEASURE_TITLES.EMPLOYEE_COUNT_END_MANAGER]
                  },
                  secondQuery: {
                    measures: [MEASURE_TITLES.EMPLOYEE_RATIO_MANAGER]
                  }
                }
              } satisfies ModuleConfig
            ]
          : []),
        {
          moduleType: DashboardModuleType.DOUBLE,
          options: {
            config: {
              titleKey: 'dashboard.workforce.spanOfControl.title',
              firstSubtitleKey:
                'dashboard.workforce.spanOfControl.average.title',
              secondSubtitleKey:
                'dashboard.workforce.spanOfControl.maximum.title',
              exploreLink: {
                measure: MEASURE_TITLES.EMPLOYEE_AVERAGE_SPAN_OF_CONTROL,
                type: EXPLORER_TYPE.BAR
              },
              benchmarkLink: {
                measure: MEASURE_TITLES.EMPLOYEE_AVERAGE_SPAN_OF_CONTROL
              },
              infoContent:
                MEASURE_DEFINITION_BY_TITLE[
                  MEASURE_TITLES.EMPLOYEE_AVERAGE_SPAN_OF_CONTROL
                ]
            },
            firstQuery: {
              measures: [MEASURE_TITLES.EMPLOYEE_AVERAGE_SPAN_OF_CONTROL]
            },
            secondQuery: {
              measures: [MEASURE_TITLES.EMPLOYEE_MAX_SPAN_OF_CONTROL]
            }
          }
        },
        {
          moduleType: DashboardModuleType.SINGLE,
          options: {
            config: {
              titleKey: 'dashboard.workforce.countOverPeriod.title',
              exploreLink: {
                measure: MEASURE_TITLES.EMPLOYEE_COUNT_OVER_PERIOD,
                type: EXPLORER_TYPE.LIST
              },
              infoContent:
                MEASURE_DEFINITION_BY_TITLE[
                  MEASURE_TITLES.EMPLOYEE_COUNT_OVER_PERIOD
                ]
            },
            query: {
              measures: [MEASURE_TITLES.EMPLOYEE_COUNT_OVER_PERIOD]
            }
          }
        }
      ]
    }
  ]
}
